import { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Button,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  HStack,
  Input,
  Grid,
  SimpleGrid,
  Text,
  Divider,
  IconButton,
  Flex,
  Spinner,
  Tooltip,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from "@chakra-ui/react";
import { ChevronDownIcon, DeleteIcon, EditIcon } from "@chakra-ui/icons";
import { Plus, Search, CalendarDays, Info, AlertTriangle } from "lucide-react";
import axios from "axios";

import {
  createCar,
  deleteCar,
  getCars,
  updateCar,
} from "../../../services/travel/carService";
import CarForm from "./CarForm";
import { CAR_CATEGORIES, Car } from "./car.types";
import CarDetailsModal from "./CarDetail";
import { normalizeCarImages } from "./dummyCarImages";
import HireCarModal from "./HireCarModal";
import { useCustomTheme } from "../../../contexts/CustomThemeContext";
import { useAuth } from "../../../auth/AuthContext";
import BusinessListingShowcaseCard from "../../Businesses/BusinessListingShowcaseCard";
import { CustomDatePicker } from "../../CustomReusables/CustomDatePicker";
import CustomTimePicker from "../../CustomReusables/CustomTimePicker";
import GlobalSpinner from "../../CustomReusables/GlobalSpinner";

const CARS_API = process.env.REACT_APP_BACKEND_URL + "/api/v1/travel/cars";
const cardsBorderRadii = "8px";

type BookingRange = {
  _id?: string;
  pickupAt?: string;
  returnAt?: string;
  status?: string;
};

type HiredPeriodInfo = {
  pickupAt: string;
  returnAt: string;
  status?: string;
};

type FeedbackModalState = {
  isOpen: boolean;
  title: string;
  description: string;
};

const ACTIVE_BOOKING_STATUSES = [
  "awaiting_owner_approval",
  "approved",
  "paid",
  "active",
];

const cleanOwnerId = (value: any) => {
  const id = String(value || "").trim();

  if (!id) return "";
  if (id.toLowerCase() === "guest") return "";

  return id;
};

const getValidOwnerId = (car: any) => {
  return cleanOwnerId(
    car?.ownerId ||
      car?.ownerUserId ||
      car?.createdBy ||
      car?.sellerUserId ||
      car?.userId ||
      "",
  );
};

const toLocalDateString = (date: Date | null) => {
  if (!date) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const buildLocalIsoDateTime = (date: Date | null, time: string) => {
  const localDate = toLocalDateString(date);

  if (!localDate || !time) return "";

  return `${localDate}T${time}:00`;
};

const formatDateTime = (value?: string) => {
  if (!value) return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatShortDateTime = (value?: string) => {
  if (!value) return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const isActiveHireStatus = (status?: string) => {
  return ACTIVE_BOOKING_STATUSES.includes(String(status || "").toLowerCase());
};

const rangesOverlap = (
  requestedPickupAt: string,
  requestedReturnAt: string,
  bookedPickupAt?: string,
  bookedReturnAt?: string,
  bufferHours = 0,
) => {
  if (
    !requestedPickupAt ||
    !requestedReturnAt ||
    !bookedPickupAt ||
    !bookedReturnAt
  ) {
    return false;
  }

  const requestedPickup = new Date(requestedPickupAt).getTime();
  const requestedReturn = new Date(requestedReturnAt).getTime();
  const bookedPickup = new Date(bookedPickupAt).getTime();
  const bookedReturn = new Date(bookedReturnAt).getTime();

  if (
    Number.isNaN(requestedPickup) ||
    Number.isNaN(requestedReturn) ||
    Number.isNaN(bookedPickup) ||
    Number.isNaN(bookedReturn)
  ) {
    return false;
  }

  const bufferMs = Number(bufferHours || 0) * 60 * 60 * 1000;
  const bookedStartWithBuffer = bookedPickup - bufferMs;
  const bookedEndWithBuffer = bookedReturn + bufferMs;

  return (
    requestedPickup < bookedEndWithBuffer &&
    requestedReturn > bookedStartWithBuffer
  );
};

const getHiredPeriodMessage = (info?: HiredPeriodInfo | null) => {
  if (!info) return "";

  return `Hired from ${formatDateTime(info.pickupAt)} to ${formatDateTime(
    info.returnAt,
  )}.`;
};

const Cars = () => {
  const { theme } = useCustomTheme();
  const { user } = useAuth();

  const [cars, setCars] = useState<Car[]>([]);
  const [selectedCar, setSelectedCar] = useState<Car | null>(null);
  const [query, setQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");

  const [pickupDate, setPickupDate] = useState<Date | null>(null);
  const [pickupTime, setPickupTime] = useState("09:00");
  const [returnDate, setReturnDate] = useState<Date | null>(null);
  const [returnTime, setReturnTime] = useState("09:00");
  const [dateValidationAttempted, setDateValidationAttempted] = useState(false);
  const dateFieldsRef = useRef<HTMLDivElement | null>(null);

  const [availabilityMap, setAvailabilityMap] = useState<
    Record<string, boolean>
  >({});
  const [hiredPeriodMap, setHiredPeriodMap] = useState<
    Record<string, HiredPeriodInfo | null>
  >({});
  const [availabilityMode, setAvailabilityMode] = useState(false);
  const [searchingAvailability, setSearchingAvailability] = useState(false);
  const [loadingCars, setLoadingCars] = useState(true);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const details = useDisclosure();
  const hire = useDisclosure();

  const [feedbackModal, setFeedbackModal] = useState<FeedbackModalState>({
    isOpen: false,
    title: "",
    description: "",
  });

  const showFeedbackModal = (title: string, description: string) => {
    setFeedbackModal({
      isOpen: true,
      title,
      description,
    });
  };

  const closeFeedbackModal = () => {
    setFeedbackModal((prev) => ({
      ...prev,
      isOpen: false,
    }));
  };

  const loggedInUserId = String(user?.uid || "").trim();
  const loggedInUserEmail = String(user?.email || "").trim();

  const pickupAt = useMemo(
    () => buildLocalIsoDateTime(pickupDate, pickupTime),
    [pickupDate, pickupTime],
  );

  const returnAt = useMemo(
    () => buildLocalIsoDateTime(returnDate, returnTime),
    [returnDate, returnTime],
  );

  const selectedPickupDate = useMemo(
    () => toLocalDateString(pickupDate),
    [pickupDate],
  );

  const selectedReturnDate = useMemo(
    () => toLocalDateString(returnDate),
    [returnDate],
  );

  const dateValidation = useMemo(() => {
    let pickupError = "";
    let returnError = "";

    if (!pickupDate) pickupError = "Pickup date is required.";
    if (!returnDate) returnError = "Return date is required.";

    if (pickupDate && returnDate) {
      const pickup = new Date(pickupAt);
      const ret = new Date(returnAt);

      if (Number.isNaN(pickup.getTime())) {
        pickupError = "Select a valid pickup date and time.";
      } else if (pickup.getTime() < Date.now() - 60 * 1000) {
        pickupError = "Pickup date and time cannot be in the past.";
      }

      if (Number.isNaN(ret.getTime())) {
        returnError = "Select a valid return date and time.";
      } else if (!pickupError && ret <= pickup) {
        returnError = "Return date and time must be after pickup.";
      }
    }

    return {
      pickupError,
      returnError,
      isValid: !pickupError && !returnError,
    };
  }, [pickupDate, returnDate, pickupAt, returnAt]);

  const canAutoSearchAvailability = useMemo(
    () => Boolean(pickupAt && returnAt && dateValidation.isValid),
    [pickupAt, returnAt, dateValidation.isValid],
  );

  useEffect(() => {
    const style = document.createElement("style");
    style.setAttribute("data-car-date-zindex-fix", "true");
    style.innerHTML = `

      /*
       * Car-hire page only.
       * Keep the calendar above the car-page controls/cards, but below the
       * application navbar/header. Scoping prevents this page from changing
       * date pickers anywhere else in the application.
       */
      .car-hire-page .react-datepicker-popper {
        z-index: 30 !important;
      }

      .car-hire-page .react-datepicker,
      .car-hire-page .react-datepicker__tab-loop {
        z-index: 30 !important;
      }

      .car-hire-page .react-datepicker {
        background: #ffffff !important;
        border-color: #CBD5E0 !important;
        box-shadow:
          0 20px 25px -5px rgba(0, 0, 0, 0.18),
          0 10px 10px -5px rgba(0, 0, 0, 0.08) !important;
        opacity: 1 !important;
      }

      .car-hire-page .react-datepicker__month-container,
      .car-hire-page .react-datepicker__header,
      .car-hire-page .react-datepicker__time-container,
      .car-hire-page .react-datepicker__time,
      .car-hire-page .react-datepicker__time-box,
      .car-hire-page .react-datepicker__time-list {
        background: #ffffff !important;
        opacity: 1 !important;
      }

      .car-hire-page .react-datepicker__header {
        border-bottom-color: #E2E8F0 !important;
      }

      .car-hire-page .react-datepicker__current-month,
      .car-hire-page .react-datepicker-time__header,
      .car-hire-page .react-datepicker-year-header,
      .car-hire-page .react-datepicker__day-name,
      .car-hire-page .react-datepicker__day:not(.react-datepicker__day--selected):not(.react-datepicker__day--keyboard-selected):not(.react-datepicker__day--in-range):not(.react-datepicker__day--in-selecting-range),
      .car-hire-page .react-datepicker__time-list-item:not(.react-datepicker__time-list-item--selected) {
        color: #1A202C !important;
      }

      .car-hire-page .react-datepicker__day--outside-month:not(.react-datepicker__day--selected) {
        color: #A0AEC0 !important;
      }

      .car-hire-page .react-datepicker__triangle {
        fill: #ffffff !important;
        color: #ffffff !important;
        stroke: #CBD5E0 !important;
      }

      .car-hire-page .react-datepicker-popper[data-placement^="bottom"]
        .react-datepicker__triangle::before,
      .car-hire-page .react-datepicker-popper[data-placement^="bottom"]
        .react-datepicker__triangle::after {
        border-bottom-color: #ffffff !important;
      }

      .car-hire-page .react-datepicker-popper[data-placement^="top"]
        .react-datepicker__triangle::before,
      .car-hire-page .react-datepicker-popper[data-placement^="top"]
        .react-datepicker__triangle::after {
        border-top-color: #ffffff !important;
      }

      .car-hire-page .react-datepicker-wrapper,
      .car-hire-page .react-datepicker__input-container {
        width: 100% !important;
      }
    `;
    document.head.appendChild(style);

    return () => style.remove();
  }, []);

  const resetDateAvailability = () => {
    setAvailabilityMap({});
    setHiredPeriodMap({});
    setAvailabilityMode(false);
  };

  const attachOwnerToCar = (
    car: any,
    fallbackOwnerId?: string,
    hiredPeriodInfo?: HiredPeriodInfo | null,
  ) => {
    const ownerId = getValidOwnerId(car) || cleanOwnerId(fallbackOwnerId);
    const hiredMessage = getHiredPeriodMessage(hiredPeriodInfo);
    const originalDescription = String(car?.description || "").trim();

    return {
      ...car,
      userId: ownerId || car?.userId || "",
      ownerId,
      ownerUserId: ownerId,
      sellerUserId: ownerId,
      createdBy: cleanOwnerId(car?.createdBy) || ownerId,

      selectedPickupAt: pickupAt,
      selectedReturnAt: returnAt,
      selectedPickupDate,
      selectedReturnDate,
      selectedPickupTime: pickupTime,
      selectedReturnTime: returnTime,

      hiredPeriodInfo: hiredPeriodInfo || null,
      hiredPeriodMessage: hiredMessage,

      description: hiredMessage
        ? [hiredMessage, originalDescription].filter(Boolean).join("\n\n")
        : originalDescription,

      ...(car?.ownerEmail || loggedInUserEmail
        ? { ownerEmail: String(car?.ownerEmail || loggedInUserEmail).trim() }
        : {}),
    };
  };

  const isOwner = (car: any) => {
    const ownerId = getValidOwnerId(car);

    return (
      !!loggedInUserId &&
      (ownerId === loggedInUserId ||
        String(car?.userId || "") === loggedInUserId ||
        String(car?.ownerId || "") === loggedInUserId ||
        String(car?.ownerUserId || "") === loggedInUserId ||
        String(car?.sellerUserId || "") === loggedInUserId ||
        String(car?.createdBy || "") === loggedInUserId ||
        (!!loggedInUserEmail &&
          String(car?.ownerEmail || "") === loggedInUserEmail))
    );
  };

  const isManualCarAvailable = (car: any) => {
    return car?.availability !== false && car?.isMaintenance !== true;
  };

  const fetchCars = async () => {
    try {
      setLoadingCars(true);

      const response = await getCars();
      const data = response.data || [];

      const normalized: Car[] = data.map((c: any) => {
        const ownerId = getValidOwnerId(c);

        return {
          ...c,
          images: normalizeCarImages(c.images, c.imageUrl),
          availability: c.availability !== false,
          category: String(c.category || "Other").trim() || "Other",
          ownerId,
          ownerUserId: ownerId,
          sellerUserId: ownerId,
          userId: ownerId || c.userId || "",
          createdBy: cleanOwnerId(c.createdBy) || ownerId,
        };
      });

      setCars(normalized);
    } finally {
      setLoadingCars(false);
    }
  };

  useEffect(() => {
    fetchCars();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreate = async (data: any) => {
    if (!loggedInUserId) return;

    const payload = {
      ...data,
      userId: loggedInUserId,
      ownerId: loggedInUserId,
      ownerUserId: loggedInUserId,
      sellerUserId: loggedInUserId,
      createdBy: loggedInUserId,
      ...(loggedInUserEmail ? { ownerEmail: loggedInUserEmail } : {}),
    };

    await createCar(payload);
    fetchCars();
    onClose();
  };

  const handleUpdate = async (data: any) => {
    if (!selectedCar || !loggedInUserId) return;

    const existingOwnerId = getValidOwnerId(selectedCar);
    const finalOwnerId = existingOwnerId || loggedInUserId;

    const payload = {
      ...data,
      userId: finalOwnerId,
      ownerId: finalOwnerId,
      ownerUserId: finalOwnerId,
      sellerUserId: finalOwnerId,
      createdBy: cleanOwnerId((selectedCar as any)?.createdBy) || finalOwnerId,
      ...(loggedInUserEmail
        ? { ownerEmail: (selectedCar as any)?.ownerEmail || loggedInUserEmail }
        : {}),
    };

    await updateCar(selectedCar._id, payload);
    fetchCars();
    onClose();
  };

  const handleDelete = async (id: string) => {
    await deleteCar(id);
    fetchCars();
  };

  const filteredByText = useMemo(() => {
    const q = query.trim().toLowerCase();

    return cars.filter((c) => {
      const carCategory = String((c as any).category || "Other").trim();
      const matchesCategory =
        categoryFilter === "all" ||
        carCategory.toLowerCase() === categoryFilter.toLowerCase();

      if (!matchesCategory) return false;
      if (!q) return true;

      return `${(c as any).make || ""} ${(c as any).model || ""} ${
        (c as any).brand || ""
      } ${carCategory} ${(c as any).pickupLocation || ""} ${
        (c as any).transmission || ""
      }`
        .toLowerCase()
        .includes(q);
    });
  }, [cars, query, categoryFilter]);

  const filteredCars = useMemo(() => {
    if (!availabilityMode) return filteredByText;

    return filteredByText;
  }, [filteredByText, availabilityMode]);

  const findOverlappingHiredPeriod = async (
    car: any,
  ): Promise<HiredPeriodInfo | null> => {
    const carId = String(car?._id || "");

    if (!carId || !pickupAt || !returnAt) return null;

    try {
      const response = await axios.get(
        `${CARS_API}/hire/availability/${carId}`,
      );
      const ranges: BookingRange[] =
        response.data?.ranges || response.data?.data || [];

      const conflict = ranges.find((range) => {
        if (!isActiveHireStatus(range.status)) return false;

        return rangesOverlap(
          pickupAt,
          returnAt,
          range.pickupAt,
          range.returnAt,
          Number(car?.bufferHours || 0),
        );
      });

      if (!conflict?.pickupAt || !conflict?.returnAt) return null;

      return {
        pickupAt: conflict.pickupAt,
        returnAt: conflict.returnAt,
        status: conflict.status,
      };
    } catch (error) {
      console.error("Failed to fetch car hire availability ranges:", error);
      return null;
    }
  };

  const searchAvailability = async (silent = false) => {
    if (!silent) setDateValidationAttempted(true);

    if (!pickupAt || !returnAt || !dateValidation.isValid) {
      if (!silent) {
        showFeedbackModal(
          "Select valid hire dates",
          dateValidation.pickupError ||
            dateValidation.returnError ||
            "Pickup and return dates are required before checking availability.",
        );
      }
      return;
    }

    const pickup = new Date(pickupAt);
    const ret = new Date(returnAt);

    if (Number.isNaN(pickup.getTime()) || Number.isNaN(ret.getTime())) {
      if (!silent) {
        showFeedbackModal(
          "Invalid hire dates",
          "Please select valid pickup and return date and time values.",
        );
      }
      return;
    }

    if (ret <= pickup) {
      if (!silent) {
        showFeedbackModal(
          "Check the return date",
          "The return date and time must be after the pickup date and time.",
        );
      }
      return;
    }

    setSearchingAvailability(true);

    try {
      const results: Record<string, boolean> = {};
      const hiredPeriods: Record<string, HiredPeriodInfo | null> = {};

      await Promise.all(
        filteredByText.map(async (car: any) => {
          const carId = String(car?._id || "");

          if (!carId) return;

          if (!isManualCarAvailable(car)) {
            results[carId] = false;
            hiredPeriods[carId] = null;
            return;
          }

          try {
            const response = await axios.post(`${CARS_API}/hire/quote`, {
              carId,
              pickupAt,
              returnAt,
              insurancePlan: "none",
              deliveryRequested: false,
              promoCode: "",
            });

            const isAvailableForDates = response.data?.ok === true;
            results[carId] = isAvailableForDates;

            if (!isAvailableForDates) {
              hiredPeriods[carId] = await findOverlappingHiredPeriod(car);
            } else {
              hiredPeriods[carId] = null;
            }
          } catch {
            results[carId] = false;
            hiredPeriods[carId] = await findOverlappingHiredPeriod(car);
          }
        }),
      );

      setAvailabilityMap(results);
      setHiredPeriodMap(hiredPeriods);
      setAvailabilityMode(true);
    } finally {
      setSearchingAvailability(false);
    }
  };

  useEffect(() => {
    if (!canAutoSearchAvailability) return;

    const timer = window.setTimeout(() => {
      searchAvailability(true);
    }, 450);

    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    canAutoSearchAvailability,
    pickupAt,
    returnAt,
    pickupTime,
    returnTime,
    filteredByText,
  ]);

  const clearAvailabilityFilter = () => {
    setAvailabilityMap({});
    setHiredPeriodMap({});
    setAvailabilityMode(false);
  };

  const openDetails = (car: Car) => {
    const carId = String((car as any)?._id || "");
    const hiredPeriodInfo = hiredPeriodMap[carId] || null;
    const manuallyAvailable = isManualCarAvailable(car);
    const availableForSelectedDates =
      availabilityMode && availabilityMap[carId] === true;
    const canHire = manuallyAvailable && availableForSelectedDates;

    const availabilityStatusLabel = !manuallyAvailable
      ? "Unavailable"
      : canHire
        ? "Available"
        : hiredPeriodInfo
          ? "Hired"
          : availabilityMode
            ? "Not available"
            : "Select dates";

    const unavailableReason = !manuallyAvailable
      ? "This car is currently unavailable."
      : hiredPeriodInfo
        ? getHiredPeriodMessage(hiredPeriodInfo)
        : availabilityMode
          ? "This car is not available for the selected dates."
          : "Select pickup and return dates to check whether this car can be hired.";

    setSelectedCar({
      ...attachOwnerToCar(car, undefined, hiredPeriodInfo),
      availability: manuallyAvailable,
      checkoutCanHire: manuallyAvailable && (!availabilityMode || canHire),
      availabilityStatusLabel,
      unavailableReason,
      isHiredForSelectedDates: Boolean(hiredPeriodInfo),
    } as any);

    details.onOpen();
  };

  const showMainDateValidation = () => {
    setDateValidationAttempted(true);

    window.setTimeout(() => {
      dateFieldsRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }, 0);
  };

  const openHire = (car: Car) => {
    const carId = String((car as any)?._id || "");
    const hiredPeriodInfo = hiredPeriodMap[carId] || null;
    const safeCar = attachOwnerToCar(car, undefined, hiredPeriodInfo);
    const manuallyAvailable = isManualCarAvailable(car);

    if (!manuallyAvailable) {
      showFeedbackModal(
        "Car unavailable",
        "This car is currently unavailable and cannot be hired.",
      );
      return;
    }

    if (availabilityMode && !availabilityMap[carId]) {
      showFeedbackModal(
        hiredPeriodInfo ? "Car already hired" : "Car unavailable",
        hiredPeriodInfo
          ? getHiredPeriodMessage(hiredPeriodInfo)
          : "This car is not available for the selected dates.",
      );
      return;
    }

    if (!dateValidation.isValid) {
      showMainDateValidation();
      details.onClose();
      return;
    }

    setSelectedCar({
      ...safeCar,
      availability: true,
      checkoutCanHire: true,
      availabilityStatusLabel: availabilityMode ? "Available" : "Select dates",
      unavailableReason: availabilityMode
        ? ""
        : "Pickup and return dates are required before checkout.",
      isHiredForSelectedDates: false,
      selectedPickupAt: pickupAt,
      selectedReturnAt: returnAt,
      selectedPickupDate,
      selectedReturnDate,
      selectedPickupTime: pickupTime,
      selectedReturnTime: returnTime,
    } as any);

    hire.onOpen();
  };

  return (
    <Box
      className="car-hire-page"
      borderRadius={cardsBorderRadii}
      boxShadow={theme.shadowSm}
      bg={theme.surface}
      minH="100vh"
      border="1px solid"
      borderColor={theme.border}
      position="relative"
      isolation="isolate"
      zIndex={0}
    >
      <Box px={4} pt={4} position="relative" zIndex={2}>
        <Grid
          ref={dateFieldsRef}
          templateColumns={{
            base: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            md: "repeat(3, minmax(0, 1fr))",
            xl: "minmax(230px, 1fr) 140px minmax(145px, 165px) 120px minmax(145px, 165px) 120px max-content",
          }}
          columnGap={3}
          rowGap={3}
          alignItems="start"
          w="100%"
        >
          <Box w="100%" minW={0}>
            <Flex
              alignItems="center"
              color={theme.textMuted}
              gap={2}
              mb={1}
              pl="2px"
              minH="18px"
            >
              <Search size={12} />
              <Text fontSize="11px" color={theme.textSubtle}>
                Search
              </Text>
            </Flex>

            <Input
              w="100%"
              h="42px"
              placeholder="Search make, model, category, location..."
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                resetDateAvailability();
              }}
              bg={theme.inputBg}
              color={theme.text}
              border="1px solid"
              borderColor={theme.inputBorder}
              borderRadius={cardsBorderRadii}
              fontWeight="600"
              boxShadow={theme.shadowSm}
              _placeholder={{ color: theme.inputPlaceholder }}
              _hover={{ borderColor: theme.inputBorderFocus }}
              _focus={{
                borderColor: theme.inputBorderFocus,
                boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
              }}
            />
          </Box>

          <Box w="100%" minW={0} position="relative">
            <Flex
              alignItems="center"
              color={theme.textMuted}
              gap={2}
              mb={1}
              pl="2px"
              minH="18px"
            >
              <Text fontSize="11px" color={theme.textSubtle}>
                Category
              </Text>
            </Flex>

            <Menu matchWidth placement="bottom-start" gutter={4}>
              {({ isOpen }) => (
                <>
                  <MenuButton
                    as={Button}
                    w="100%"
                    h="42px"
                    minW={0}
                    px={3}
                    bg={theme.inputBg}
                    color={theme.text}
                    border="1px solid"
                    borderColor={
                      isOpen ? theme.inputBorderFocus : theme.inputBorder
                    }
                    borderRadius={cardsBorderRadii}
                    fontWeight="600"
                    fontSize="sm"
                    textAlign="left"
                    boxShadow={
                      isOpen
                        ? `0 0 0 1px ${theme.inputBorderFocus}`
                        : theme.shadowSm
                    }
                    rightIcon={
                      <ChevronDownIcon
                        boxSize={4}
                        transform={isOpen ? "rotate(180deg)" : "rotate(0deg)"}
                        transition="transform 0.18s ease"
                      />
                    }
                    _hover={{
                      bg: theme.inputBg,
                      borderColor: theme.inputBorderFocus,
                    }}
                    _active={{ bg: theme.inputBg }}
                    _focusVisible={{
                      borderColor: theme.inputBorderFocus,
                      boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
                    }}
                    sx={{
                      "& > span:first-of-type": {
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      },
                    }}
                  >
                    {categoryFilter === "all"
                      ? "All categories"
                      : categoryFilter}
                  </MenuButton>

                  <MenuList
                    w="100%"
                    minW="100%"
                    maxW="100%"
                    p={1}
                    mt={0}
                    maxH="260px"
                    overflowY="auto"
                    overflowX="hidden"
                    bg={theme.surface}
                    color={theme.text}
                    border="1px solid"
                    borderColor={theme.borderStrong}
                    borderRadius={cardsBorderRadii}
                    boxShadow={theme.shadowMd}
                    zIndex={20}
                    sx={{
                      scrollbarWidth: "thin",
                      "&::-webkit-scrollbar": {
                        width: "6px",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        background: theme.borderStrong,
                        borderRadius: {cardsBorderRadii}
                      },
                    }}
                  >
                    {[
                      { value: "all", label: "All categories" },
                      ...CAR_CATEGORIES.map((category) => ({
                        value: category,
                        label: category,
                      })),
                    ].map((option) => {
                      const selected = categoryFilter === option.value;

                      return (
                        <MenuItem
                          key={option.value}
                          onClick={() => {
                            setCategoryFilter(option.value);
                            resetDateAvailability();
                          }}
                          w="100%"
                          minW={0}
                          px={3}
                          py={2}
                          borderRadius={cardsBorderRadii}
                          bg={selected ? theme.surfaceAlt : "transparent"}
                          color={selected ? theme.primary : theme.text}
                          fontWeight={selected ? "700" : "600"}
                          fontSize="sm"
                          overflow="hidden"
                          textOverflow="ellipsis"
                          whiteSpace="nowrap"
                          _hover={{
                            bg: theme.hover,
                            color: theme.text,
                          }}
                          _focus={{
                            bg: theme.hover,
                            color: theme.text,
                          }}
                        >
                          {option.label}
                        </MenuItem>
                      );
                    })}
                  </MenuList>
                </>
              )}
            </Menu>
          </Box>

          <Box w="100%" minW={0} position="relative">
            <Flex
              alignItems="center"
              color={theme.textMuted}
              gap={2}
              mb={1}
              pl="2px"
              minH="18px"
            >
              <CalendarDays size={12} />
              <Text fontSize="11px" color={theme.textSubtle}>
                Pickup Date
              </Text>
            </Flex>

            <Box
              w="100%"
              className={
                dateValidationAttempted && dateValidation.pickupError
                  ? "car-date-input car-date-input--invalid"
                  : "car-date-input"
              }
              sx={
                dateValidationAttempted && dateValidation.pickupError
                  ? {
                      "& input": {
                        borderColor: `${theme.danger} !important`,
                        boxShadow: `0 0 0 1px ${theme.danger} !important`,
                        borderRadius: "8px !important",
                      },
                      "& input:hover, & input:focus": {
                        borderColor: `${theme.danger} !important`,
                        boxShadow: `0 0 0 1px ${theme.danger} !important`,
                      },
                    }
                  : undefined
              }
            >
              <CustomDatePicker
                width="100%"
                selected={pickupDate}
                onChange={(date) => {
                  setPickupDate(date);
                  if (date && returnDate && returnDate <= date) {
                    setReturnDate(null);
                  }
                  resetDateAvailability();
                }}
                placeholder="Pickup date"
              />
            </Box>

            {dateValidationAttempted && dateValidation.pickupError && (
              <Text mt={1} fontSize="xs" color={theme.danger} fontWeight="600">
                {dateValidation.pickupError}
              </Text>
            )}
          </Box>

          <Box w="100%" minW={0} maxW={{ xl: "120px" }} whiteSpace="nowrap">
            <CustomTimePicker
              label="Pickup Time"
              value={pickupTime}
              onChange={(value) => {
                setPickupTime(value);
                resetDateAvailability();
              }}
            />
          </Box>

          <Box w="100%" minW={0} position="relative">
            <Flex
              alignItems="center"
              color={theme.textMuted}
              gap={2}
              mb={1}
              pl="2px"
              minH="18px"
            >
              <CalendarDays size={12} />
              <Text fontSize="11px" color={theme.textSubtle}>
                Return Date
              </Text>
            </Flex>

            <Box
              w="100%"
              className={
                dateValidationAttempted && dateValidation.returnError
                  ? "car-date-input car-date-input--invalid"
                  : "car-date-input"
              }
              sx={
                dateValidationAttempted && dateValidation.returnError
                  ? {
                      "& input": {
                        borderColor: `${theme.danger} !important`,
                        boxShadow: `0 0 0 1px ${theme.danger} !important`,
                        borderRadius: "8px !important",
                      },
                      "& input:hover, & input:focus": {
                        borderColor: `${theme.danger} !important`,
                        boxShadow: `0 0 0 1px ${theme.danger} !important`,
                      },
                    }
                  : undefined
              }
            >
              <CustomDatePicker
                width="100%"
                selected={returnDate}
                onChange={(date) => {
                  setReturnDate(date);
                  resetDateAvailability();
                }}
                placeholder="Return date"
              />
            </Box>

            {dateValidationAttempted && dateValidation.returnError && (
              <Text mt={1} fontSize="xs" color={theme.danger} fontWeight="600">
                {dateValidation.returnError}
              </Text>
            )}
          </Box>

          <Box w="100%" minW={0} maxW={{ xl: "120px" }} whiteSpace="nowrap">
            <CustomTimePicker
              label="Return Time"
              value={returnTime}
              onChange={(value) => {
                setReturnTime(value);
                resetDateAvailability();
              }}
            />
          </Box>

          <Flex
            display={{ base: "none", xl: "flex" }}
            gap={3}
            align="flex-end"
            alignSelf="end"
            whiteSpace="nowrap"
            minW="max-content"
          >
            {availabilityMode && (
              <Button
                h="42px"
                px={4}
                size="sm"
                borderRadius={cardsBorderRadii}
                variant="outline"
                onClick={clearAvailabilityFilter}
                borderColor={theme.borderStrong}
                color={theme.text}
                bg={theme.surface}
                _hover={{ bg: theme.hover }}
              >
                Clear filter
              </Button>
            )}

            {!!loggedInUserId && (
              <Button
                h="42px"
                px={4}
                size="sm"
                color="white"
                bgGradient="linear(to-r, gray.800, gray.900)"
                border="1px solid"
                borderColor="whiteAlpha.200"
                borderRadius={cardsBorderRadii}
                fontWeight="700"
                leftIcon={
                  <Box
                    as="span"
                    display="inline-flex"
                    alignItems="center"
                    justifyContent="center"
                    transition="transform 0.25s ease"
                    className="add-car-icon"
                  >
                    <Plus size={15} />
                  </Box>
                }
                _hover={{
                  bgGradient: "linear(to-r, gray.700, black)",
                  borderColor: "whiteAlpha.300",
                  transform: "translateY(-1px)",
                  boxShadow: theme.shadowMd || theme.shadowSm,
                  ".add-car-icon": {
                    transform: "rotate(90deg)",
                  },
                }}
                _active={{
                  transform: "translateY(0)",
                  boxShadow: theme.shadowSm,
                }}
                onClick={() => {
                  setSelectedCar(null);
                  onOpen();
                }}
              >
                Add Car
              </Button>
            )}
          </Flex>
        </Grid>

        <Flex
          display={{ base: "flex", xl: "none" }}
          mt={3}
          gap={3}
          flexWrap="wrap"
          align="center"
          justify="flex-start"
          w="100%"
        >
          <Button
            display={{ base: "inline-flex", md: "none" }}
            h="40px"
            px={4}
            size="sm"
            borderRadius={cardsBorderRadii}
            color={theme.textOnPrimary}
            bgImage={theme.gradients.primaryButton}
            boxShadow={theme.shadowSm}
            _hover={{
              bgImage: theme.gradients.primaryButtonHover,
              transform: "translateY(-1px)",
              boxShadow: theme.shadowMd,
            }}
            _active={{ transform: "translateY(0)" }}
            onClick={() => searchAvailability(false)}
            isLoading={searchingAvailability}
          >
            Search availability
          </Button>

          {availabilityMode && (
            <Button
              h="40px"
              px={4}
              size="sm"
              borderRadius={cardsBorderRadii}
              variant="outline"
              onClick={clearAvailabilityFilter}
              borderColor={theme.borderStrong}
              color={theme.text}
              bg={theme.surface}
              _hover={{ bg: theme.hover }}
            >
              Clear filter
            </Button>
          )}

          {!!loggedInUserId && (
            <Button
              h="40px"
              px={4}
              size="sm"
              color="white"
              bgGradient="linear(to-r, gray.800, gray.900)"
              border="1px solid"
              borderColor="whiteAlpha.200"
              borderRadius={cardsBorderRadii}
              fontWeight="700"
              leftIcon={
                <Box
                  as="span"
                  display="inline-flex"
                  alignItems="center"
                  justifyContent="center"
                  transition="transform 0.25s ease"
                  className="add-car-icon"
                >
                  <Plus size={15} />
                </Box>
              }
              _hover={{
                bgGradient: "linear(to-r, gray.700, black)",
                borderColor: "whiteAlpha.300",
                transform: "translateY(-1px)",
                boxShadow: theme.shadowMd || theme.shadowSm,
                ".add-car-icon": {
                  transform: "rotate(90deg)",
                },
              }}
              _active={{
                transform: "translateY(0)",
                boxShadow: theme.shadowSm,
              }}
              onClick={() => {
                setSelectedCar(null);
                onOpen();
              }}
            >
              Add Car
            </Button>
          )}
        </Flex>

        <Box
          mt={3}
          px={3}
          py={2}
          border="1px solid"
          borderColor={theme.border}
          borderRadius={cardsBorderRadii}
          bg={theme.surfaceAlt}
        >
          <Text fontSize="sm" color={theme.text} fontWeight="700">
            Hire dates are required before checkout.
          </Text>
          <Text mt={0.5} fontSize="xs" color={theme.textSubtle}>
            Select pickup and return dates here. Availability is checked
            automatically once the dates and times are valid, and the same dates
            are carried into the Hire window automatically.
          </Text>
        </Box>

        {availabilityMode && (
          <Text mt={2} fontSize="sm" color={theme.textSubtle}>
            Showing results for{" "}
            <b>
              {selectedPickupDate} {pickupTime}
            </b>{" "}
            to{" "}
            <b>
              {selectedReturnDate} {returnTime}
            </b>
            .
          </Text>
        )}
      </Box>

      <Divider mt={4} borderColor={theme.divider} />

      <Box p={4} position="relative" zIndex={1}>
        {loadingCars ? (
          <Flex
            minH="calc(100vh - 260px)"
            w="100%"
            align="center"
            justify="center"
          >
            <GlobalSpinner />
          </Flex>
        ) : (
          <>
            <SimpleGrid
              columns={{ base: 1, sm: 2, lg: 3, xl: 4 }}
              spacing={4}
              alignItems="stretch"
            >
              {filteredCars.map((car: Car) => {
                const carId = String((car as any)._id || "");
                const manuallyAvailable = isManualCarAvailable(car);
                const availableForSelectedDates =
                  availabilityMode && availabilityMap[carId] === true;
                const hiredPeriodInfo = hiredPeriodMap[carId] || null;
                const canHire = manuallyAvailable && availableForSelectedDates;
                const canManage = isOwner(car);

                const title =
                  `${(car as any).brand ? `${(car as any).brand} ` : ""}${
                    (car as any).make || ""
                  } ${(car as any).model || ""}`.trim();

                const pricePerDay = Number((car as any).pricePerDay || 0);

                const hero = Array.isArray((car as any).images)
                  ? (car as any).images[0]
                  : (car as any).imageUrl || "";

                const hiredMessage = getHiredPeriodMessage(hiredPeriodInfo);

                const badgeLabel: string | undefined = !manuallyAvailable
                  ? "Unavailable"
                  : canHire
                    ? "Available"
                    : hiredPeriodInfo
                      ? "Hired"
                      : availabilityMode
                        ? "Not available"
                        : undefined;

                const actionLabel = !manuallyAvailable
                  ? "View"
                  : canHire
                    ? "Hire"
                    : availabilityMode
                      ? "View"
                      : "Choose dates";

                return (
                  <Box key={car._id} h="100%">
                    <BusinessListingShowcaseCard
                      title={title || "Car Hire"}
                      subtitle={`${(car as any).pickupLocation || "-"} • ${
                        (car as any).transmission || "-"
                      } • ${(car as any).seats || "-"} seats`}
                      description={
                        hiredMessage ||
                        (car as any).description ||
                        `${(car as any).year || "Available"} ${
                          (car as any).make || "car"
                        } for hire.`
                      }
                      image={hero}
                      price={
                        pricePerDay > 0
                          ? `MWK ${pricePerDay.toLocaleString()} / day`
                          : "Price on request"
                      }
                      location={(car as any).pickupLocation || ""}
                      category={(car as any).category || "Other"}
                      badgeLabel={badgeLabel}
                      statusTone={
                        canHire
                          ? "success"
                          : !manuallyAvailable ||
                              !!hiredPeriodInfo ||
                              availabilityMode
                            ? "warning"
                            : "default"
                      }
                      actionLabel={actionLabel}
                      compactAction
                      onCardClick={() => openDetails(car)}
                      onActionClick={() => {
                        if (actionLabel === "Choose dates") {
                          showMainDateValidation();
                          return;
                        }

                        if (canHire) {
                          openHire(car);
                          return;
                        }

                        openDetails(car);
                      }}
                      footerActions={
                        hiredPeriodInfo || canManage ? (
                          <>
                            {hiredPeriodInfo && (
                              <Tooltip
                                hasArrow
                                placement="top"
                                label={`Already hired: ${formatShortDateTime(
                                  hiredPeriodInfo.pickupAt,
                                )} → ${formatShortDateTime(
                                  hiredPeriodInfo.returnAt,
                                )}`}
                              >
                                <IconButton
                                  aria-label="Hired period details"
                                  icon={<Info size={14} />}
                                  size="sm"
                                  variant="outline"
                                  borderRadius={cardsBorderRadii}
                                  borderColor="orange.200"
                                  color="orange.700"
                                  bg="orange.50"
                                  _hover={{ bg: "orange.100" }}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                  }}
                                />
                              </Tooltip>
                            )}

                            {canManage && (
                              <>
                                <IconButton
                                  aria-label="Edit car"
                                  icon={<EditIcon />}
                                  size="sm"
                                  variant="outline"
                                  borderRadius={cardsBorderRadii}
                                  borderColor={theme.borderStrong}
                                  color={theme.text}
                                  bg={theme.surfaceAlt}
                                  _hover={{ bg: theme.hover }}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setSelectedCar(
                                      attachOwnerToCar(
                                        car,
                                        loggedInUserId,
                                        hiredPeriodInfo,
                                      ),
                                    );
                                    onOpen();
                                  }}
                                />

                                <IconButton
                                  aria-label="Delete car"
                                  icon={<DeleteIcon />}
                                  size="sm"
                                  borderRadius={cardsBorderRadii}
                                  colorScheme="red"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleDelete(car._id);
                                  }}
                                />
                              </>
                            )}
                          </>
                        ) : undefined
                      }
                    />
                  </Box>
                );
              })}
            </SimpleGrid>

            {filteredCars.length === 0 && (
              <Box mt={10} textAlign="center" color={theme.textSubtle}>
                No cars found.
              </Box>
            )}
          </>
        )}

        {searchingAvailability && (
          <HStack mt={4} color={theme.textSubtle}>
            <Spinner size="sm" />
            <Text>Checking availability…</Text>
          </HStack>
        )}
      </Box>

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay bg={theme.overlay} />
        <ModalContent
          bg={theme.surface}
          border="1px solid"
          borderColor={theme.border}
        >
          <ModalHeader color={theme.text}>
            {selectedCar ? "Edit Car" : "Add Car"}
          </ModalHeader>
          <ModalCloseButton color={theme.text} />
          <ModalBody>
            <CarForm
              initialData={selectedCar}
              onSubmit={selectedCar ? handleUpdate : handleCreate}
            />
          </ModalBody>
        </ModalContent>
      </Modal>

      <CarDetailsModal
        isOpen={details.isOpen}
        onClose={details.onClose}
        car={selectedCar}
        onHire={(car: Car) => {
          details.onClose();
          openHire(car);
        }}
      />

      <HireCarModal
        isOpen={hire.isOpen}
        onClose={hire.onClose}
        car={selectedCar}
      />

      <Modal
        isOpen={feedbackModal.isOpen}
        onClose={closeFeedbackModal}
        isCentered
        size={{ base: "sm", md: "md" }}
      >
        <ModalOverlay bg={theme.overlay} />
        <ModalContent
          mx={{ base: 4, md: 0 }}
          bg={theme.surface}
          border="1px solid"
          borderColor="orange.300"
          borderRadius={cardsBorderRadii}
          boxShadow={theme.shadowMd}
        >
          <ModalHeader color={theme.text}>
            <HStack spacing={3}>
              <Flex
                w="38px"
                h="38px"
                align="center"
                justify="center"
                borderRadius={cardsBorderRadii}
                bg="orange.100"
                color="orange.600"
                flexShrink={0}
              >
                <AlertTriangle size={19} />
              </Flex>
              <Text fontSize="lg" fontWeight="800">
                {feedbackModal.title}
              </Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton color={theme.text} />
          <ModalBody pb={2}>
            <Text color={theme.textMuted} lineHeight="1.7">
              {feedbackModal.description}
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button
              onClick={closeFeedbackModal}
              color={theme.textOnPrimary}
              bgImage={theme.gradients.primaryButton}
              _hover={{ bgImage: theme.gradients.primaryButtonHover }}
              borderRadius={cardsBorderRadii}
            >
              Okay
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Cars;
